import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg, today } from "@/lib/api";
import { Field, TableWrap, Empty, th, td, tr } from "@/components/common";
import { TargetPicker } from "@/components/bank";

export function ManualEntryDialog({ open, accounts, meta, defaultAccount, onClose, onSaved }) {
  const [f, setF] = useState({});
  useEffect(() => { if (open) setF({ account_id: defaultAccount || accounts[0]?.id || "", date: today(), ref: "", mode: "UPI", direction: "debit", amount: "", remarks: "" }); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/bank/txns", { ...f, amount: Number(f.amount) });
      toast.success(data.result === "auto" ? `Entry added & auto-matched: ${data.allocation?.target}` : "Entry added — sent to Suspense for allocation");
      onSaved();
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-card" data-testid="manual-entry-dialog">
        <DialogHeader><DialogTitle>Add Manual Bank Entry</DialogTitle><DialogDescription>Statement me missing entry yahan bharein — save hote hi auto-match chalega, warna Suspense me jayegi.</DialogDescription></DialogHeader>
        <form onSubmit={save} className="grid gap-3 sm:grid-cols-2">
          <Field label="Bank / Cash Account *" className="sm:col-span-2"><select className="native-select" data-testid="manual-account" value={f.account_id || ""} onChange={(e) => setF({ ...f, account_id: e.target.value, mode: accounts.find((a) => a.id === e.target.value)?.account_type === "cash" ? "Cash" : f.mode })}>{accounts.map((a) => <option key={a.id} value={a.id}>{a.bank_name}{a.account_number ? ` · ${a.account_number}` : ""}</option>)}</select></Field>
          <Field label="Date *"><Input type="date" required data-testid="manual-date" value={f.date || ""} onChange={set("date")} /></Field>
          <Field label="UTR / Ref / Cheque No"><Input data-testid="manual-ref" value={f.ref || ""} onChange={set("ref")} /></Field>
          <Field label="Mode"><select className="native-select" data-testid="manual-mode" value={f.mode} onChange={set("mode")}>{meta.modes.map((m) => <option key={m}>{m}</option>)}</select></Field>
          <Field label="Type"><select className="native-select" data-testid="manual-direction" value={f.direction} onChange={set("direction")}><option value="debit">Debit (−) Outflow</option><option value="credit">Credit (+) Inflow</option></select></Field>
          <Field label="Amount (₹) *"><Input type="number" step="0.01" min="0.01" required data-testid="manual-amount" value={f.amount} onChange={set("amount")} /></Field>
          <Field label="Remarks / Narration" className="sm:col-span-2"><Input data-testid="manual-remarks" value={f.remarks || ""} onChange={set("remarks")} placeholder="e.g. UPI/RAJ FUELS/diesel MP09AB1234" /></Field>
          <DialogFooter className="sm:col-span-2"><Button type="button" variant="outline" onClick={onClose}>Cancel</Button><Button type="submit" data-testid="manual-save-btn">Save Entry</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const RULE_TYPES = ["vendor_payment", "supplier_payment", "party_receipt", "driver", "vehicle_expense", "office_expense", "no_ledger"];

function RuleDialog({ open, initial, meta, onClose, onSaved }) {
  const [f, setF] = useState({});
  const [opts, setOpts] = useState(null);
  useEffect(() => {
    if (!open) return;
    setF({ keyword: "", type: "vendor_payment", driver_txn_type: "advance", category: "", note: "", active: true, party_name: "", ...(initial || {}), rule: true });
    api.get("/bank/options").then((r) => setOpts(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [open, initial]);
  const set = (x) => setF((p) => ({ ...p, ...x }));
  const save = async (e) => {
    e.preventDefault();
    const body = { keyword: f.keyword, type: f.type, vendor_id: f.vendor_id || null, supplier_id: f.supplier_id || null, party_name: f.party_name || "", driver_id: f.driver_id || null, driver_txn_type: f.driver_txn_type || "advance", vehicle_id: f.vehicle_id || null, category: f.category || "", note: f.note || "", active: f.active !== false };
    try {
      const { data } = initial ? await api.put(`/bank/rules/${initial.id}`, body) : await api.post("/bank/rules", body);
      toast.success(`Rule saved — ${data.applied} suspense entries cleared`);
      onSaved();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const types = meta.alloc_types.filter((t) => RULE_TYPES.includes(t.key));
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-card" data-testid="rule-dialog">
        <DialogHeader><DialogTitle>{initial ? "Edit Auto-Match Rule" : "New Auto-Match Rule"}</DialogTitle><DialogDescription>Bank remarks me ye text mile to entry seedha chuni hui ledger me jayegi.</DialogDescription></DialogHeader>
        {opts ? (
          <form onSubmit={save} className="grid gap-3">
            <Field label="Remarks me ye text ho (UPI name / keyword) *"><Input required data-testid="rule-keyword" value={f.keyword || ""} onChange={(e) => set({ keyword: e.target.value })} placeholder="e.g. RAJ FUELS" /></Field>
            <Field label="Ledger *"><select className="native-select" data-testid="rule-type" value={f.type} onChange={(e) => set({ type: e.target.value })}>{types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}</select></Field>
            <TargetPicker f={f} set={set} opts={opts} meta={meta} />
            {f.type === "no_ledger" && <Field label="Label"><Input data-testid="rule-note" value={f.note || ""} onChange={(e) => set({ note: e.target.value })} placeholder="e.g. Bank Charges" /></Field>}
            <label className="flex items-center gap-2 text-sm"><Switch data-testid="rule-active" checked={f.active !== false} onCheckedChange={(v) => set({ active: v })} />Active</label>
            <DialogFooter><Button type="button" variant="outline" onClick={onClose}>Cancel</Button><Button type="submit" data-testid="rule-save-btn">Save & Apply</Button></DialogFooter>
          </form>
        ) : <div className="text-sm text-muted-foreground">Loading…</div>}
      </DialogContent>
    </Dialog>
  );
}

export function RulesTab({ meta, onChanged }) {
  const [rules, setRules] = useState([]);
  const [dlg, setDlg] = useState({ open: false, initial: null });
  const load = () => api.get("/bank/rules").then((r) => setRules(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const done = () => { setDlg({ open: false, initial: null }); load(); onChanged(); };
  const del = async (r) => {
    if (!window.confirm(`Delete rule "${r.keyword}"? Already matched entries stay as they are.`)) return;
    try { await api.delete(`/bank/rules/${r.id}`); toast.success("Rule deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = async (r, active) => {
    try { const { data } = await api.put(`/bank/rules/${r.id}`, { ...r, active }); toast.success(active ? `Rule on — ${data.applied} entries cleared` : "Rule paused"); load(); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  const runAll = async () => {
    try { const { data } = await api.post("/bank/rules/apply"); toast.success(`${data.applied} suspense entries cleared`); onChanged(); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <div className="mb-3 flex flex-wrap justify-end gap-2">
        <Button size="sm" variant="outline" data-testid="rules-apply-btn" onClick={runAll}><Wand2 className="mr-1 h-4 w-4" />Apply rules to Suspense</Button>
        <Button size="sm" data-testid="add-rule-btn" onClick={() => setDlg({ open: true, initial: null })}><Plus className="mr-1 h-4 w-4" />New Rule</Button>
      </div>
      <TableWrap>
        <thead><tr>{["Keyword", "Ledger", "Target", "Matched", "Active", ""].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
        <tbody>{rules.map((r) => (
          <tr key={r.id} className={tr} data-testid={`rule-row-${r.id}`}>
            <td className={`${td} font-mono text-xs font-semibold`}>{r.keyword}</td>
            <td className={`${td} text-xs`}>{r.type_label}</td>
            <td className={`${td} text-xs`}>{r.target}</td>
            <td className={td} data-testid={`rule-hits-${r.id}`}>{r.hits || 0}</td>
            <td className={td}><Switch data-testid={`rule-toggle-${r.id}`} checked={r.active} onCheckedChange={(v) => toggle(r, v)} /></td>
            <td className={td}><div className="flex justify-end gap-1">
              <Button size="icon" variant="ghost" data-testid={`rule-edit-${r.id}`} onClick={() => setDlg({ open: true, initial: r })}><Pencil className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" data-testid={`rule-delete-${r.id}`} onClick={() => del(r)}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
            </div></td>
          </tr>
        ))}</tbody>
      </TableWrap>
      {!rules.length && <Empty testid="rules-empty" text="No rules yet — create one, or tick 'Is naam ke liye yaad rakhein' while resolving a suspense entry" />}
      <RuleDialog open={dlg.open} initial={dlg.initial} meta={meta} onClose={() => setDlg({ open: false, initial: null })} onSaved={done} />
    </div>
  );
}
